import { baseApi } from './base.api';
import { API_VERSION, PATH_KNOWLEDGE_BASE_API } from '../constants/api-routes';
import { getApiVersion } from 'src/utils/apiVersion';
import type { Integration as KnowledgeBaseCollector, KBFile } from 'src/utils/fileUtils';

export type { KnowledgeBaseCollector };

type Id = number | string;

export interface KnowledgeBase {
  id: number;
  account_id: number;
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
  // Loaded with load_projects.
  projects?: {
    id: number;
    name: string;
  }[];
}

export interface KnowledgeBaseRequest {
  load_projects?: boolean;
  project_ids?: number[] | string;
  name_filter?: string;
  page_size?: number;
  page?: number;
}

export interface KnowledgeBaseResponse {
  id: string | number;
  items?: KnowledgeBase[];
  page_info?: { has_next_page?: boolean };
}

interface KnowledgeBaseFilesPage {
  items?: KBFile[];
  page_info?: { has_next_page?: boolean };
}

export interface CollectorConsentLink {
  authorize_url?: string;
}

export interface CollectorStatus {
  status?: string;
}

function makeNamesUnique<T extends { id: Id; name: string }>(items: T[]): T[] {
  const usedNames = new Map();

  return [...items]
    .sort((a, b) => Number(a.id) - Number(b.id))
    .map((item) => {
      const originalName = item.name;

      if (!usedNames.has(originalName)) {
        usedNames.set(originalName, 0);

        return {
          ...item,
          name: originalName,
        };
      }

      let count = usedNames.get(originalName) + 1;
      let newName = `${originalName} (${count})`;

      while (usedNames.has(newName)) {
        count += 1;
        newName = `${originalName} (${count})`;
      }
      usedNames.set(originalName, count);
      usedNames.set(newName, 0);
      return {
        ...item,
        name: newName,
      };
    });
}

export const knowledgeBaseService = baseApi.injectEndpoints({
  endpoints: (build) => {
    return {
      getKnowledgeBase: build.query<KnowledgeBaseResponse, KnowledgeBaseRequest | void>({
        query: (params) => {
          const version = getApiVersion();
          const request: KnowledgeBaseRequest = params || {};

          return {
            url: `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}`,
            params: {
              ...request,
              page_size: request.page_size || 100,
            },
          };
        },
        providesTags: ['KnowledgeBase'],
      }),
      getKnowledgeBaseById: build.query<KnowledgeBase, { id: Id; load_projects?: boolean }>({
        query: ({ id, load_projects }) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}/${id}`,
            params: {
              ...(load_projects !== undefined && { load_projects }),
            },
          };
        },
        providesTags: ['KnowledgeBaseById'],
      }),
      getKnowledgeBaseFiles: build.query<
        KBFile[],
        { id: Id; collector_id?: string; sync?: boolean }
      >({
        async queryFn({ id, collector_id, sync }, _queryApi, _extraOptions, fetchWithBQ) {
          const version = getApiVersion();
          const url = `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}/${id}/files`;

          if (version !== API_VERSION.V2) {
            const result = await fetchWithBQ({
              url,
              params: { sync, ...(collector_id && { collector_id }) },
            });
            return result.error ? { error: result.error } : { data: result.data as KBFile[] };
          }

          // V2 paginates files; the tree view needs the full set, so fetch every page.
          const PAGE_SIZE = 100;
          let page = 1;
          let items: KBFile[] = [];

          while (true) {
            const result = await fetchWithBQ({
              url,
              params: { sync, page, page_size: PAGE_SIZE, ...(collector_id && { collector_id }) },
            });

            if (result.error) {
              return { error: result.error };
            }

            const filesPage = result.data as KnowledgeBaseFilesPage;

            items = items.concat(filesPage?.items || []);

            if (!filesPage?.page_info?.has_next_page) {
              break;
            }

            page += 1;
          }

          return { data: items };
        },
        providesTags: ['KnowledgeBaseFiles'],
      }),
      getKnowledgeBaseFileById: build.query<KBFile, { id: Id; file_id: Id }>({
        query: ({ id, file_id }) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}/${id}/files/${file_id}`,
          };
        },
        providesTags: ['KnowledgeBaseFileById'],
      }),
      getKnowledgeBaseFileContent: build.query<string, { url: string }>({
        query: ({ url }) => {
          return { url, method: 'GET' };
        },
        extraOptions: {
          responseHandler: 'text',
          mode: 'cors',
        },
        keepUnusedDataFor: 0,
        providesTags: ['KnowledgeBaseFileContent'],
      }),
      addKnowledgeBaseFile: build.mutation<KBFile, { id: Id; body: FormData }>({
        query: ({ id, body }) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}/${id}/files/file`,
            method: 'POST',
            body: body,
            formData: true,
          };
        },
        async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
          try {
            const { data: file } = await queryFulfilled;

            dispatch(
              knowledgeBaseService.util.updateQueryData(
                'getKnowledgeBaseFiles',
                { id, sync: false },
                (draft) => {
                  draft.push(file);
                },
              ),
            );
          } catch (err) {
            console.error('Error add knowledge base files:', err);
          }
        },
      }),
      addKnowledgeBaseDocument: build.mutation<KBFile, { id: Id; body: Record<string, unknown> }>({
        query: ({ id, body }) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}/${id}/files/document`,
            method: 'POST',
            body: body,
          };
        },
        async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
          try {
            const { data: file } = await queryFulfilled;

            dispatch(
              knowledgeBaseService.util.updateQueryData(
                'getKnowledgeBaseFiles',
                { id, sync: false },
                (draft) => {
                  draft.push(file);
                },
              ),
            );
          } catch (err) {
            console.error('Error knowledge base document:', err);
          }
        },
        invalidatesTags: ['KnowledgeBaseFiles'],
      }),
      updateKnowledgeBaseFiles: build.mutation<unknown, { id: Id; body: unknown }>({
        query: ({ id, body }) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}/${id}/files`,
            method: 'PATCH',
            body: body,
          };
        },
        invalidatesTags: ['KnowledgeBaseFiles'],
      }),
      updateKnowledgeBaseFile: build.mutation<
        unknown,
        { knowledgeBaseId: Id; fileId: Id; body: unknown }
      >({
        query: ({ knowledgeBaseId, fileId, body }) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}/${knowledgeBaseId}/files/${fileId}`,
            method: 'PATCH',
            body: body,
          };
        },
        invalidatesTags: ['KnowledgeBaseFiles'],
      }),
      deleteKnowledgeBaseFiles: build.mutation<unknown, { id: Id; body: Id[] }>({
        query: ({ id, body }) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}/${id}/files`,
            method: 'DELETE',
            body: body,
          };
        },
        async onQueryStarted({ id, body }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            knowledgeBaseService.util.updateQueryData(
              'getKnowledgeBaseFiles',
              { id, sync: false },
              (draft) => draft.filter((file) => !body.includes(file.id)),
            ),
          );

          try {
            await queryFulfilled;
          } catch (err) {
            patchResult.undo();
            console.error('Error deleting file:', err);
          }
        },
        invalidatesTags: ['KnowledgeBaseFiles'],
      }),
      createKnowledgeBase: build.mutation<KnowledgeBase, { name: string }>({
        query: ({ name }) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}`,
            method: 'POST',
            body: {
              name,
            },
          };
        },
        invalidatesTags: () => [{ type: 'KnowledgeBase' }],
      }),
      deleteKnowledgeBase: build.mutation<{ status?: string }, { knowledge_base_id: Id }>({
        query: ({ knowledge_base_id }) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}/${knowledge_base_id}`,
            method: 'DELETE',
          };
        },
        invalidatesTags: () => [{ type: 'KnowledgeBase' }],
      }),
      editKnowledgeBaseName: build.mutation<unknown, { knowledge_base_id: Id; name: string }>({
        query: ({ knowledge_base_id, name }) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}/${knowledge_base_id}`,
            method: 'PATCH',
            body: {
              name,
            },
          };
        },
        invalidatesTags: () => [{ type: 'KnowledgeBase' }],
      }),
      updateKnowledgeBaseProjects: build.mutation<
        unknown,
        { knowledge_base_id: Id; projects_ids: Id[] }
      >({
        query: ({ knowledge_base_id, projects_ids }) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}/${knowledge_base_id}/projects`,
            method: 'PUT',
            body: {
              projects_ids,
            },
          };
        },
        invalidatesTags: () => [{ type: 'KnowledgeBase' }, { type: 'KnowledgeBaseById' }],
      }),
      getKnowledgeBaseCollectors: build.query<
        KnowledgeBaseCollector[],
        { id: Id; include_data?: boolean }
      >({
        query: ({ id, include_data = false }) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}/${id}/collectors`,
            params: { include_data },
          };
        },
        transformResponse: (collectors: KnowledgeBaseCollector[]) => makeNamesUnique(collectors),
        providesTags: ['KnowledgeBaseCollectors'],
      }),
      createKnowledgeBaseCollector: build.mutation<
        KnowledgeBaseCollector,
        { id: Id; body: Record<string, unknown> }
      >({
        query: ({ id, body }) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}/${id}/collectors`,
            method: 'POST',
            body: body,
          };
        },
        invalidatesTags: ['KnowledgeBaseCollectors', 'KnowledgeBaseFiles'],
      }),
      updateKnowledgeBaseCollector: build.mutation<
        KnowledgeBaseCollector,
        { knowledgeBaseId: Id; collectorId: Id; body: Record<string, unknown> }
      >({
        query: ({ knowledgeBaseId, collectorId, body }) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}/${knowledgeBaseId}/collectors/${collectorId}`,
            method: 'PATCH',
            body: body,
          };
        },
        invalidatesTags: ['KnowledgeBaseCollectors', 'KnowledgeBaseFiles'],
      }),
      deleteKnowledgeBaseCollector: build.mutation<
        unknown,
        { knowledgeBaseId: Id; collectorId: Id }
      >({
        query: ({ knowledgeBaseId, collectorId }) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}/${knowledgeBaseId}/collectors/${collectorId}`,
            method: 'DELETE',
          };
        },
        invalidatesTags: ['KnowledgeBaseCollectors', 'KnowledgeBaseFiles'],
      }),
      setupKnowledgeBaseCollector: build.mutation<
        unknown,
        { knowledgeBaseId: Id; collectorId: Id; body: Record<string, unknown> }
      >({
        query: ({ knowledgeBaseId, collectorId, body }) => {
          const version = API_VERSION.V1;

          return {
            url: `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}/${knowledgeBaseId}/collectors/${collectorId}/setup`,
            method: 'POST',
            body: body,
          };
        },
        invalidatesTags: ['KnowledgeBaseCollectors', 'KnowledgeBaseFiles'],
      }),
      runKnowledgeBaseCollector: build.mutation<
        unknown,
        { knowledgeBaseId: Id; collectorId: Id; body?: Record<string, unknown> }
      >({
        query: ({ knowledgeBaseId, collectorId, body = {} }) => {
          const version = API_VERSION.V1;

          return {
            url: `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}/${knowledgeBaseId}/collectors/${collectorId}/run`,
            method: 'POST',
            body: body,
          };
        },
        invalidatesTags: ['KnowledgeBaseCollectors', 'KnowledgeBaseFiles'],
      }),
      getKnowledgeBaseCollectorZendeskConsentLink: build.query<
        CollectorConsentLink,
        { knowledgeBaseId: Id; collectorId: Id }
      >({
        query: ({ knowledgeBaseId, collectorId }) => {
          const version = getApiVersion();

          return {
            url: `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}/${knowledgeBaseId}/collectors/${collectorId}/${PATH_KNOWLEDGE_BASE_API.COLLECTOR_ZENDESK_OAUTH_AUTHORIZE_URL}`,
          };
        },
      }),
      getCollectorsStatus: build.query<CollectorStatus, { collectorId: Id }>({
        query: ({ collectorId }) => {
          const version = API_VERSION.V1;

          return {
            url: `${version}/${PATH_KNOWLEDGE_BASE_API.ROOT}/collectors/${collectorId}/status`,
          };
        },
        providesTags: ['KnowledgeBaseCollectors'],
      }),
    };
  },
});

export const {
  useGetKnowledgeBaseQuery,
  useGetKnowledgeBaseByIdQuery,
  useCreateKnowledgeBaseMutation,
  useDeleteKnowledgeBaseMutation,
  useEditKnowledgeBaseNameMutation,
  useUpdateKnowledgeBaseProjectsMutation,
  useGetKnowledgeBaseFilesQuery,
  useLazyGetKnowledgeBaseFilesQuery,
  useAddKnowledgeBaseFileMutation,
  useUpdateKnowledgeBaseFilesMutation,
  useUpdateKnowledgeBaseFileMutation,
  useDeleteKnowledgeBaseFilesMutation,
  useAddKnowledgeBaseDocumentMutation,
  useGetKnowledgeBaseCollectorsQuery,
  useLazyGetKnowledgeBaseCollectorsQuery,
  useLazyGetKnowledgeBaseCollectorZendeskConsentLinkQuery,
  useCreateKnowledgeBaseCollectorMutation,
  useUpdateKnowledgeBaseCollectorMutation,
  useDeleteKnowledgeBaseCollectorMutation,
  useSetupKnowledgeBaseCollectorMutation,
  useRunKnowledgeBaseCollectorMutation,
  useLazyGetKnowledgeBaseFileByIdQuery,
  useLazyGetKnowledgeBaseFileContentQuery,
  useLazyGetCollectorsStatusQuery,
} = knowledgeBaseService;
